import { createGlobalStyle } from "styled-components";
import theme from "styled-theming";
import colors from "./colors";
import "./fonts.css"
import { device, hexToRgb } from "./helpers";

export const NavBarHeight = "80px"
export const PageHeaderContainerHeightWithMargin = "104px";
export const IndentedMarginTop = "30px";
export const IndentedMarginTopSmall = "1rem";
export const UserThemeLocalStorageKey = "userTheme";

export enum ThemeType {
    light = "light",
    dark = "dark"
}

const textColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.grey_4
});

const backgroundColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark
});

const footerBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.grey_5,
    [ThemeType.dark]: colors.dark
})

const footerBorderColor = theme("mode", {
    [ThemeType.light]: colors.purplePrimary,
    [ThemeType.dark]: colors.white
})

const footerColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.white
})

export const bpmnPaletteBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.grey_5,
    [ThemeType.dark]: colors.dark2
})

const bpmnPaletteBackgroundColorHover = theme("mode", {
    [ThemeType.light]: colors.dark5,
    [ThemeType.dark]: colors.dark
})

const bpmnPopupShadowColor = theme("mode", {
    [ThemeType.light]: colors.dark5,
    [ThemeType.dark]: colors.dark3
})

export const bpmnPaletteColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.grey_4
})

const bpmnElementAccentColor = theme("mode", {
    [ThemeType.light]: colors.orangePrimary,
    [ThemeType.dark]: colors.purpleTertiary
})

const bpmnSearchResultSelectedColor = theme("mode", {
    [ThemeType.light]: colors.orangePrimary,
    [ThemeType.dark]: colors.purpleSecondary
});

const bpmnSearchBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.grey_1
});

const bpmnSearchBackgroundColorSelected= theme("mode", {
    [ThemeType.light]: colors.orangePrimary,
    [ThemeType.dark]: colors.purpleSecondary
})

const bpmnContextPadBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark2
})

const bpmnContextPadEntryHoverBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark
})

const bpmnLabelColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.grey_4
})

const bpmnNotRunLabelColor = theme("mode", {
    [ThemeType.light]: colors.grey_1,
    [ThemeType.dark]: colors.white
})

const bpmnElementColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark5
})

const bpmnDropAllowedColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark
})

export const datePickerBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark2
})

const datePickerDayColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.grey_2
})

const datePickerDayHoverBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.grey_3,
    [ThemeType.dark]: colors.dark
})

const datePickerDisabledDayColor = theme("mode", {
    [ThemeType.light]: colors.grey_2,
    [ThemeType.dark]: colors.dark
})

const datePickerHeaderBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.grey_2,
    [ThemeType.dark]: colors.dark2
})

const datePickerHeaderColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.grey_2
})

export const linkColor = theme("mode", {
    [ThemeType.light]: colors.purplePrimary,
    [ThemeType.dark]: colors.purpleTertiary
})

const datePickerNavigationColor = theme("mode", {
    [ThemeType.light]: colors.grey_1,
    [ThemeType.dark]: colors.grey_1
})

const datePickerInputContainerBackgroundColor = theme("mode", {
    [ThemeType.light]: colors.white,
    [ThemeType.dark]: colors.dark2
})

const datePickerInputContainerColor = theme("mode", {
    [ThemeType.light]: colors.black,
    [ThemeType.dark]: colors.white
})

export const GlobalStyle = createGlobalStyle`
    :root {
        --toastify-toast-width: 420px;
        --toastify-color-dark: ${colors.dark2};
        --base-font-size: 16px;
        --header-1-font-size: 2rem; // base 16px + 16px = 32px
        --header-2-font-size: 1.5rem; // base 16px + 8px = 24px
        --header-3-font-size: 1.25rem; // base 16px + 4px = 20px
        --header-4-font-size: 1.125rem; // base 16px + 2px = 18px
        --small-text-font-size: 0.875rem; // base 16px - 2px = 14px
    }
    html, body {
        font-family: 'Gilroy-Regular', "regular-fallback";
        margin: 0;
        padding: 0;
        font-size: var(--base-font-size);
        color: ${textColor};
        background-color: ${backgroundColor};
    }
    html {
        box-sizing: border-box;

    }
    body {
        overflow: overlay;
        font-family: "Gilroy-Regular", "regular-fallback";

    }
    *, *:before, *:after {
        box-sizing: inherit;
    }
    h1,h2,h3,h4 {
        font-family: "Gilroy-Medium", "medium-fallback";
    }
    h1 {
        font-size: var(--header-1-font-size);
        font-weight: 500;
        margin: 0;
    }
    h2 {
        font-size: var(--header-2-font-size);
        font-weight: 500;
        margin: 0;
    }
    h3 {
        font-size: var(--header-3-font-size);
        font-weight: 500;
        margin: 0;
    }
    h4 {
        font-size: var(--header-4-font-size);
        font-weight: 500;
        margin: 0;
    }
    hr {
        border: 0.4px solid ${colors.grey_4};
        margin: 25px 0;
    }
    a {
        color: ${linkColor};
    }
    .pointer {
        cursor: pointer;
    }
    .full-width {
        width: 100%;
    }
    ::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }
    /* Track */
    ::-webkit-scrollbar-track {
        background: transparent;
    }
    
    /* Handle */
    ::-webkit-scrollbar-thumb {
        background: ${colors.grey_2};
        border-radius: 30px;
    }

    /* Handle on hover */
    ::-webkit-scrollbar-thumb:hover {
        background: ${colors.grey_1}; 
    }
    #footer {
        border-top: 1px solid;
        background-color: ${footerBackgroundColor};
        border-color: ${footerBorderColor};
        color: ${footerColor};
      
    }
    a {
        text-decoration: none;
    }
    .djs-parent {
        --palette-entry-color: ${bpmnPaletteColor};
        --palette-entry-hover-color: ${bpmnElementAccentColor};
        --palette-background-color: ${bpmnPaletteBackgroundColor};
        --palette-entry-selected-color: ${bpmnElementAccentColor};
        --context-pad-entry-hover-background-color: ${bpmnContextPadEntryHoverBackgroundColor};
        
        --shape-drop-allowed-fill-color: ${bpmnDropAllowedColor};
        --shape-connect-allowed-fill-color: ${bpmnDropAllowedColor};
        --shape-resize-preview-stroke-color: ${bpmnElementAccentColor};
        --shape-attach-allowed-stroke-color: ${bpmnElementAccentColor};
        
        --bendpoint-fill-color: ${bpmnElementAccentColor};
        --bendpoint-stroke-color: ${bpmnElementAccentColor};

        --element-dragger-color: ${bpmnElementAccentColor};
        --element-hover-outline-fill-color: ${bpmnElementAccentColor};
        --element-selected-outline-stroke-color: ${bpmnElementAccentColor};
        
        --popup-header-entry-selected-color: ${bpmnElementAccentColor};
        --popup-background-color: ${bpmnPaletteBackgroundColor};
        --popup-entry-hover-color: ${bpmnPaletteBackgroundColorHover};
        --popup-border-color: ${colors.transparent};
        --popup-shadow-color: ${bpmnPopupShadowColor};
        --resizer-fill-color: ${bpmnElementAccentColor};
        --resizer-stroke-color: ${bpmnElementAccentColor};

        --search-container-border-color: ${bpmnElementAccentColor};
        --search-container-box-shadow-color: ${bpmnElementAccentColor};
        --search-result-selected-color: ${bpmnSearchResultSelectedColor};
        --snap-line-stroke-color: ${bpmnElementAccentColor};
        
        .djs-palette //Moves the palette towards the corner and makes it double column. It seems the palette is not always wrapping when it should, so let's force it to two:
        {
            top: 70px;
            left: 10px;
            width: 123px !important;
            border: 1px solid ${colors.grey_2};
            border-radius: 25px;
            @media ${device.desktop} {
                left: 38px;
                top: 100px;
                width: 110px !important;
            }
            .entry {
                width: 36px;
                @media ${device.desktop} {
                    width: 46px;
                }
            }
        }
        .djs-palette-entries {
            padding: 5px;
        }
        .djs-palette-toggle {
            visibility: collapse !important; //Hide the collapse button on the toolbox.
        }
        .highlight-obsolete-task:not(.djs-connection) .djs-visual > :nth-child(1) {
            stroke: ${colors.orangeSecondary}  !important; /* color border yellow */
        }
        .djs-overlay {
            pointer-events: none;
        }
        .djs-context-pad {
            background-color: ${bpmnContextPadBackgroundColor};
        }
        .djs-context-pad .entry {
            background: transparent;
            box-shadow: none;
        }
        .djs-popup {
            .entry.selected  {
                .djs-popup-label, [class*="icon"]:before {
                    color: ${colors.white};
                }
                &[data-id='red-color'] {
                background-color: ${colors.warningSecondary};
                }
                &[data-id='orange-color'] {
                    background-color: ${colors.orangeSecondary};
                }
                &[data-id='green-color'] {
                    background-color: ${colors.greenSecondary};
                }
                &[data-id='blue-color'] {
                    background-color: ${colors.blueSecondary};
                }
                &[data-id='purple-color'] {
                    background-color: ${colors.purpleSecondary};
                }
                &[data-id='pink-color'] {
                    background-color: ${colors.pinkSecondary};
                }
            }
        }
        .djs-minimap {
            z-index: 3;
            background-color: ${bpmnElementColor} !important;
        }
        .djs-minimap:not(.open) {
            overflow: hidden;
            display: none;
        }
        .djs-minimap .viewport-dom {
            border: 2px solid ${colors.purplePrimary};
        }
        .highlight-error-scope .djs-visual > :nth-child(1) {
            stroke: ${colors.warningPrimary}  !important; /* color border red */
        }
        .djs-element:not(.djs-connection) .djs-visual {
            .djs-label {
                fill: ${bpmnLabelColor} !important;
            }

            /* BpmnViewer (Process execution) special text color cases */
            /* Success */
            rect[style*="fill: ${hexToRgb(colors.uiGreenLight)};"] + .djs-label,
            circle[style*="fill: ${hexToRgb(colors.uiGreenLight)}"] + .djs-label,
            /* Running */
            rect[style*="fill: ${hexToRgb(colors.uiOrange)};"] + .djs-label,
            circle[style*="fill: ${hexToRgb(colors.uiOrange)}"] + .djs-label {
                fill: ${colors.black} !important;
            }
            /* Error */
            rect[style*="stroke: ${hexToRgb(colors.uiWarning)};"] + .djs-label {
                fill: ${colors.uiWarning} !important;
            }
            rect[style*="stroke: ${hexToRgb(colors.uiWarningDark)};"] + .djs-label {
                fill: ${colors.white} !important;
            }
            rect[style*="stroke: ${hexToRgb(colors.warningTertiary)};"] + .djs-label {
                fill: ${colors.warningTertiary} !important;
            }
            /* Not run */
            rect[style*="stroke: ${hexToRgb(colors.grey_1)};"] + .djs-label,
            circle[style*="stroke: ${hexToRgb(colors.grey_1)}"] + .djs-label {
                fill: ${bpmnNotRunLabelColor} !important;
            }
        }
        .djs-direct-editing-parent {
            background-color: ${bpmnElementColor} !important;
        }
        .djs-search-result {
            background: ${bpmnSearchBackgroundColor};
            &-selected {
                background: ${bpmnSearchBackgroundColorSelected};
            }
        }
        .djs-search-overlay {
            background: ${bpmnSearchResultSelectedColor};
            opacity: 25%;
        }
    }

    .collapse-css-transition {
        transition: height 280ms cubic-bezier(0.4, 0, 0.2, 1);
    }
    .Toastify__toast-container {
        z-index: 99999; // Raise toast above all elements including modals
    }
    .Toastify__progress-bar {
        right: 0;
        transform-origin: right;
    }
    .Toastify__progress-bar--success {
        background-color: ${colors.uiGreen};
    }
    .Toastify__progress-bar--warning {
        background-color: ${colors.orangePrimary};
    }
    .monaco-editor {
         .view-overlays .current-line {
            border-color: transparent !important;
         }
        .margin-view-overlays .current-line-margin {
            border-color: transparent !important;
        }
    }
    .react-datepicker-wrapper {
        .react-datepicker__input-container {
            input {
                height: 40px !important;
                border-radius: 20px !important;
                background-color: ${datePickerInputContainerBackgroundColor} !important;
                color: ${datePickerInputContainerColor};
                padding-left: 15px;
            }
        }
    }
    .react-datepicker {
        .react-datepicker__header {
            background-color: ${datePickerHeaderBackgroundColor} !important;
            .react-datepicker__current-month, .react-datepicker__day-name {
                color: ${datePickerHeaderColor} !important;
            }
        }
        background-color: ${datePickerBackgroundColor} !important;
        .react-datepicker__day {
            color: ${datePickerDayColor} !important;
            &:hover {
                background-color: ${datePickerDayHoverBackgroundColor} !important;
            }
        }
        .react-datepicker__day--disabled {
            color: ${datePickerDisabledDayColor} !important;
        }
        .react-datepicker__navigation--previous {
            border-right-color: ${datePickerNavigationColor} !important;
        }
        .react-datepicker__navigation--next {
            border-left-color: ${datePickerNavigationColor} !important;
        }
    }
    a[href='http://bpmn.io'] {
        color: black !important;
    }
    .cc-revoke // Override cookieconsent "Cookie policy"-link style to match the theme
    {
        font-family: "Gilroy-Regular", "regular-fallback";
        font-size: 1rem;
        font-weight: 400;
        font-style: normal;
        line-height: 36px;
        text-decoration: underline;
        color: ${colors.black} !important;
        background-color: ${colors.transparent} !important;
    }
`

